import type { Candidate, EducationEntry } from '@core/types';

export type ExtraColumnKey =
  | 'phone'
  | 'linkedin_url'
  | 'notes'
  | 'tags'
  | 'years_exp'
  | 'recruiter_rating';

export const EXTRA_COLUMN_META: { key: ExtraColumnKey; label: string; widthClass?: string }[] = [
  { key: 'phone', label: 'Phone' },
  { key: 'linkedin_url', label: 'LinkedIn' },
  { key: 'notes', label: 'Notes' },
  { key: 'tags', label: 'Tags' },
  { key: 'years_exp', label: 'Years exp.' },
  { key: 'recruiter_rating', label: 'Rating' },
];

export type BaseColumnKey =
  | 'email'
  | 'current_title'
  | 'current_company'
  | 'location'
  | 'source'
  | 'skills'
  | 'rolefit'
  | 'resume'
  | 'created';

const ALL_BASE_COLUMN_KEYS = new Set<BaseColumnKey>([
  'email',
  'current_title',
  'current_company',
  'location',
  'source',
  'skills',
  'rolefit',
  'resume',
  'created',
]);

/** Migrate legacy `role` (title+company combined) to separate keys. */
function normalizeHiddenBaseColumns(raw: unknown): BaseColumnKey[] {
  if (!Array.isArray(raw)) return [];
  const next: BaseColumnKey[] = [];
  for (const k of raw) {
    if (k === 'role') {
      next.push('current_title', 'current_company');
    } else if (typeof k === 'string' && ALL_BASE_COLUMN_KEYS.has(k as BaseColumnKey)) {
      next.push(k as BaseColumnKey);
    }
  }
  return [...new Set(next)];
}

/** Per-user row for the Candidates list “Manage Rights” modal (local policy; masking + record ACL targets). */
export type CandidateListUserAccess = {
  viewEmail: boolean;
  viewPhone: boolean;
  viewNotes: boolean;
  /** View this list / open candidate rows (default true when unset). */
  canRead: boolean;
  /** Create or edit candidates from this list context. */
  canWrite: boolean;
  /** Remove candidate records (subject to server RBAC). */
  canDelete: boolean;
  /** Operate dependent objects (e.g. applications, notes, child pipelines). */
  canOperateChildren: boolean;
};

export function normalizeCandidateListUserAccess(
  raw: Partial<CandidateListUserAccess> | undefined,
): CandidateListUserAccess {
  return {
    viewEmail: Boolean(raw?.viewEmail),
    viewPhone: Boolean(raw?.viewPhone),
    viewNotes: Boolean(raw?.viewNotes),
    canRead: raw?.canRead !== false,
    canWrite: Boolean(raw?.canWrite),
    canDelete: Boolean(raw?.canDelete),
    canOperateChildren: Boolean(raw?.canOperateChildren),
  };
}

function migrateUserFieldAccess(raw: unknown): Record<string, CandidateListUserAccess> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, CandidateListUserAccess> = {};
  for (const [id, v] of Object.entries(raw)) {
    out[id] = normalizeCandidateListUserAccess(v as Partial<CandidateListUserAccess>);
  }
  return out;
}

export interface CandidatesTablePolicy {
  /** Extra data columns beyond defaults */
  extraColumns: ExtraColumnKey[];
  /** Base columns hidden for everyone (IDs from BaseColumnKey) */
  hiddenBaseColumns: BaseColumnKey[];
  maskEmail: boolean;
  maskPhone: boolean;
  /** Per org user id — unmask grants + record-level toggles for this list */
  userFieldAccess: Record<string, CandidateListUserAccess>;
}

export const defaultCandidatesTablePolicy = (): CandidatesTablePolicy => ({
  extraColumns: [],
  hiddenBaseColumns: [],
  maskEmail: false,
  maskPhone: false,
  userFieldAccess: {},
});

export function loadCandidatesTablePolicy(orgId: string): CandidatesTablePolicy {
  try {
    const raw = localStorage.getItem(`ggj:candidates:tablePolicy:${orgId}`);
    if (!raw) return defaultCandidatesTablePolicy();
    const p = JSON.parse(raw) as Partial<CandidatesTablePolicy>;
    return {
      ...defaultCandidatesTablePolicy(),
      ...p,
      extraColumns: Array.isArray(p.extraColumns) ? p.extraColumns : [],
      hiddenBaseColumns: normalizeHiddenBaseColumns(p.hiddenBaseColumns),
      userFieldAccess: migrateUserFieldAccess(p.userFieldAccess),
    };
  } catch {
    return defaultCandidatesTablePolicy();
  }
}

export function saveCandidatesTablePolicy(orgId: string, policy: CandidatesTablePolicy) {
  localStorage.setItem(`ggj:candidates:tablePolicy:${orgId}`, JSON.stringify(policy));
}

export function maskEmailValue(email: string | null | undefined, mask: boolean): string {
  if (!email?.trim()) return '—';
  if (!mask) return email;
  const [u, d] = email.split('@');
  if (!d) return '•••';
  const safeU = u.length <= 2 ? '••' : `${u.slice(0, 2)}•••`;
  return `${safeU}@${d}`;
}

export function maskPhoneValue(phone: string | null | undefined, mask: boolean): string {
  if (!phone?.trim()) return '—';
  if (!mask) return phone;
  const d = phone.replace(/\D/g, '');
  if (d.length < 4) return '••••';
  return `••••${d.slice(-4)}`;
}

export function candidateRowForExport(c: Candidate): Record<string, string | number | null> {
  return {
    Name: c.full_name ?? '',
    Email: c.email ?? '',
    Phone: c.phone ?? '',
    'Current title': c.current_title ?? '',
    Company: c.current_company ?? '',
    Location: c.location ?? '',
    Source: (c.source_channel as string) ?? '',
    Skills: (c.parsed_skills ?? []).join('; '),
    Tags: (c.tags ?? []).join('; '),
    'Years exp': c.years_exp ?? '',
    Rating: c.recruiter_rating ?? '',
    Notes: c.notes ?? '',
    LinkedIn: c.linkedin_url ?? '',
    'Created when': c.created_at ?? '',
  };
}

/** Human-readable education for tabular exports (full object includes this). */
export function formatEducationForExport(education: EducationEntry[] | null | undefined): string {
  if (!education?.length) return '';
  return education
    .map((e) => {
      const parts = [e.degree?.trim(), e.institution?.trim()].filter(Boolean);
      const head = parts.join(' — ');
      return e.year != null ? `${head} (${e.year})` : head;
    })
    .join(' | ');
}

/**
 * Full candidate record as flat columns (entire persisted object model, excluding UI-only optional fields when absent).
 */
export function candidateFullFlatForExport(c: Candidate): Record<string, string | number | boolean> {
  return {
    id: c.id,
    org_id: c.org_id,
    created_by: c.created_by,
    full_name: c.full_name ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    linkedin_url: c.linkedin_url ?? '',
    source_channel: (c.source_channel as string) ?? '',
    source: c.source ?? '',
    utm_source: c.utm_source ?? '',
    utm_medium: c.utm_medium ?? '',
    utm_campaign: c.utm_campaign ?? '',
    current_company: c.current_company ?? '',
    current_title: c.current_title ?? '',
    location: c.location ?? '',
    avatar_url: c.avatar_url ?? '',
    base_resume_url: c.base_resume_url ?? '',
    recruiter_rating: c.recruiter_rating ?? '',
    parsed_skills: (c.parsed_skills ?? []).join('; '),
    years_exp: c.years_exp ?? '',
    education: formatEducationForExport(c.education),
    tags: (c.tags ?? []).join('; '),
    notes: c.notes ?? '',
    is_active: c.is_active,
    created_at: c.created_at ?? '',
    updated_at: c.updated_at ?? '',
    async_ai_work_status: c.async_ai_work_status ?? '',
    async_ai_work_operation: c.async_ai_work_operation ?? '',
    async_ai_work_error: c.async_ai_work_error ?? '',
    async_ai_work_updated_at: c.async_ai_work_updated_at ?? '',
  };
}

/** Stable column order for CSV / Excel (object keys order not guaranteed). */
export const CANDIDATE_FULL_EXPORT_COLUMN_ORDER: (keyof ReturnType<typeof candidateFullFlatForExport>)[] = [
  'id',
  'org_id',
  'created_by',
  'full_name',
  'email',
  'phone',
  'linkedin_url',
  'source_channel',
  'source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'current_company',
  'current_title',
  'location',
  'avatar_url',
  'base_resume_url',
  'recruiter_rating',
  'parsed_skills',
  'years_exp',
  'education',
  'tags',
  'notes',
  'is_active',
  'created_at',
  'updated_at',
  'async_ai_work_status',
  'async_ai_work_operation',
  'async_ai_work_error',
  'async_ai_work_updated_at',
];
